import {
  ContentPageTheme,
  PublicReviewWidget,
  PublicReviewWidgetReview,
  ReviewSortType,
  ReviewWidgetConfig,
} from "@/services/bedrock";

export interface DummyReview extends PublicReviewWidgetReview {
  approvedForSocial: boolean;
  shareabilityScore: number;
  link?: string;
}

export const DUMMY_REVIEWS: DummyReview[] = [
  {
    displayName: "Ashley R.",
    shareabilityScore: 0.82,
    approvedForSocial: true,
    content:
      "He was absolutely incredible from the very first phone call until the day we finally closed on our house. My husband and I were so stressed out because our previous lender messed up our paperwork, but he stepped in and fixed everything in less than a week. He explained the interest rates clearly and he never made us feel stupid for asking questions. I would highly recommend his services to anyone looking to buy a home in this crazy market.",
    condensedQuote:
      "Alex Smith was absolutely incredible ... [and] stepped in and fixed everything in less than a week.",
    sourceSystem: "GL",
    link: "https://socialcoach.io",
    ratingType: "star",
    starRating: 4.5,
    publishedAt: "2025-11-12T00:00:00Z",
  },
  {
    displayName: "Morgan K.",
    shareabilityScore: 0.9,
    approvedForSocial: true,
    content:
      "I can't say enough good things about working with her for our refinancing. She saved us hundreds of dollars a month, which is amazing because we really needed the extra breathing room in our budget this year. Honestly, she is the most responsive professional I have ever dealt with. She answered texts at 9 PM on a weekend just to make sure we didn't lose our rate lock.",
    condensedQuote:
      "Alex Smith saved us hundreds of dollars a month ... [and] is the most responsive professional I have ever dealt with.",
    sourceSystem: "FB",
    link: "https://socialcoach.io",
    ratingType: "star",
    starRating: 5,
    publishedAt: "2025-10-28T00:00:00Z",
  },
  {
    displayName: "Jordan T.",
    shareabilityScore: 0.7,
    approvedForSocial: false,
    content:
      "They handled our commercial loan and the team was just phenomenal. To be completely honest, we had no idea what we were doing when we walked into their office. They took the time to walk us through the underwriting process step-by-step. If you are a small business owner looking for a mortgage broker who actually cares about your success, you need to call them today.",
    condensedQuote:
      "[Alex Smith's team] handled our commercial loan and ... took the time to walk us through the underwriting process step-by-step.",
    sourceSystem: "SC",
    link: "https://socialcoach.io",
    ratingType: "nps",
    npsScore: 10,
    npsCategory: "promoter",
    publishedAt: "2025-10-15T00:00:00Z",
  },
  {
    displayName: "Patricia N.",
    shareabilityScore: 0.95,
    approvedForSocial: true,
    content:
      "She helped my parents buy their retirement condo last month. Because they are elderly and don't use computers well, she actually drove out to their house to get the wet signatures. It was the kindest gesture. She made a confusing process totally seamless for them.",
    condensedQuote:
      "Alex Smith helped my parents buy their retirement condo ... [and] actually drove out to their house to get the wet signatures.",
    sourceSystem: "ZL",
    link: "https://socialcoach.io",
    ratingType: "ten_point",
    tenPointRating: 9,
    publishedAt: "2025-09-18T00:00:00Z",
  },
  {
    displayName: "Kevin D.",
    shareabilityScore: 0.6,
    approvedForSocial: false,
    content:
      "He got our construction loan approved in record time. I mean, the paperwork for building a house is usually a total nightmare, but somehow he made it feel like a breeze. He was just so efficient and organized. We are practically framing the house already and it's all thanks to his hard work.",
    condensedQuote:
      "Alex Smith got our construction loan approved in record time ... [and] made it feel like a breeze.",
    sourceSystem: "SC",
    link: "https://socialcoach.io",
    ratingType: "star",
    starRating: 4.5,
    publishedAt: "2025-09-05T00:00:00Z",
  },
  {
    displayName: "Amanda S.",
    shareabilityScore: 0.85,
    approvedForSocial: true,
    content:
      "I was skeptical about using an independent loan officer instead of a big bank, but he totally changed my mind. He found a down payment assistance program that saved us $10,000. He is definitely a hidden gem in the mortgage industry.",
    condensedQuote:
      "Alex Smith found a down payment assistance program that saved us $10,000 ... [and] is definitely a hidden gem in the mortgage industry.",
    sourceSystem: "GL",
    link: "https://socialcoach.io",
    ratingType: "star",
    starRating: 4.5,
    publishedAt: "2025-08-22T00:00:00Z",
  },
  {
    displayName: "Nicole H.",
    shareabilityScore: 0.98,
    approvedForSocial: true,
    content:
      "She got our jumbo loan approved even when two other major banks turned us down because of my complex self-employment income history. My accountant was being difficult with the tax transcripts, but she personally called him to sort it out. I honestly do not think we would be living in our dream home right now if it wasn't for her persistence and knowledge of underwriting guidelines.",
    condensedQuote:
      "Alex Smith got our jumbo loan approved even when two other major banks turned us down ... [due to] her persistence and knowledge of underwriting guidelines.",
    sourceSystem: "FB",
    link: "https://socialcoach.io",
    ratingType: "ten_point",
    tenPointRating: 10,
    publishedAt: "2025-08-10T00:00:00Z",
  },
  {
    displayName: "Brian F.",
    shareabilityScore: 0.5,
    approvedForSocial: false,
    content:
      "He can be a bit blunt over email, which threw me off at first, but he absolutely delivers results. He locked our rate at the perfect moment right before the market spiked last Tuesday. If you want someone to hold your hand and chat for hours, he might not be your guy, but if you want the absolute best deal on a mortgage, he is the only one you should call.",
    condensedQuote:
      "Alex Smith locked our rate at the perfect moment right before the market spiked ... [and] if you want the absolute best deal on a mortgage, [he] is the only one you should call.",
    sourceSystem: "SC",
    link: "https://socialcoach.io",
    ratingType: "text",
    publishedAt: "2025-07-30T00:00:00Z",
  },
];

export const findTheme = (themes: ContentPageTheme[], id?: number | null) =>
  id ? themes.find(theme => theme.id === id) : undefined;

type SortableReview = Pick<DummyReview, "shareabilityScore" | "publishedAt">;

const byNewest = (a: SortableReview, b: SortableReview) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? "");

export const sortSampleReviews = <T extends SortableReview>(reviews: T[], sortType: ReviewSortType): T[] =>
  [...reviews].sort(
    sortType === "featured" ? (a, b) => b.shareabilityScore - a.shareabilityScore || byNewest(a, b) : byNewest
  );

export const SAMPLE_THEME: ContentPageTheme = {
  id: 0,
  playerId: null,
  coachId: null,
  name: "Default",
  backgroundColor: "#ffffff",
  textColor: "#111827",
  primaryCtaColor: "#2563eb",
  primaryCtaTextColor: "#ffffff",
  showEhLenderLogo: false,
  showEhOpportunityLogo: false,
  createdDate: "",
  updatedDate: "",
  headerMedia: [],
  footerMedia: [],
};

type SummarySettings = Pick<
  ReviewWidgetConfig,
  "approvedForSocialOnly" | "showStarSummary" | "showSentimentSummary" | "showNpsSummary"
>;

export const buildSampleSummary = (config: SummarySettings): PublicReviewWidget["summary"] => {
  const approvedRatio = config.approvedForSocialOnly
    ? DUMMY_REVIEWS.filter(review => review.approvedForSocial).length / DUMMY_REVIEWS.length
    : 1;

  const summary: PublicReviewWidget["summary"] = {};

  if (config.showStarSummary) {
    summary.star = {
      rating: 4.7,
      totalResponses: Math.round(47 * approvedRatio),
    };
  }
  if (config.showSentimentSummary) {
    const totalResponses = Math.round(47 * approvedRatio);
    const positiveCount = Math.round(44 * approvedRatio);
    summary.sentiment = {
      rating: totalResponses > 0 ? Math.round((positiveCount / totalResponses) * 1000) / 10 : 0,
      totalResponses,
    };
  }
  if (config.showNpsSummary) {
    summary.nps = {
      rating: 72,
      totalResponses: Math.round(38 * approvedRatio),
    };
  }

  return Object.keys(summary).length > 0 ? summary : null;
};
